import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaBell, FaSync, FaCheck } from 'react-icons/fa';
import { calendarService } from '../../services/calendar/calendarService';
import logger from '../../utils/logger';
import { toast } from 'react-toastify';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 1.2rem;
`;

const Title = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin: 0 0 1rem 0;
  line-height: 1.5;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  flex: 1;
  min-width: 120px;
  padding: 0.75rem;
  background-color: white;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 0.25rem;
`;

const UrlContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const UrlInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: 'Courier New', monospace;
  background-color: white;
  color: ${({ theme }) => theme.colors.text};
  min-width: 0; /* Permet au flex de réduire l'input si nécessaire */
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background-color: white;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  width: 100%; /* Pleine largeur sur mobile */
  
  @media (min-width: 768px) {
    width: auto; /* Largeur automatique sur desktop */
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RegenerateButton = styled(IconButton)`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 0.5rem;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.warning};
    color: white;
    border-color: ${({ theme }) => theme.colors.warning};
  }
`;

const InstructionsContainer = styled.div`
  margin-top: 1rem;
`;

const InstructionSection = styled.div`
  margin-bottom: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  overflow: hidden;
  background-color: white;
`;

const InstructionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.border};
  }
`;

const InstructionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
`;

const InstructionContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${({ $isOpen }) => ($isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding: ${({ $isOpen }) => ($isOpen ? '1rem' : '0 1rem')};
`;

const InstructionSteps = styled.ol`
  margin: 0;
  padding-left: 1.5rem;
  
  li {
    margin-bottom: 0.75rem;
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.text};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => `${theme.colors.error}10`};
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.error};
  margin-bottom: 1rem;
`;

interface CalendarStats {
  totalEvents: number;
  notifications: number;
  actions: number;
}

const CalendarSync: React.FC = () => {
  const [calendarUrl, setCalendarUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<CalendarStats>({ totalEvents: 0, notifications: 0, actions: 0 });
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState<boolean>(false);

  // Charger l'URL du calendrier au montage du composant
  useEffect(() => {
    loadCalendarUrl();
    loadStats();
  }, []);

  const loadCalendarUrl = async () => {
    try {
      setLoading(true);
      setError(null);
      const url = await calendarService.getCalendarFeedUrl();
      setCalendarUrl(url);
      logger.info('URL du calendrier chargée avec succès');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du chargement de l\'URL';
      setError(errorMessage);
      logger.error('Erreur lors du chargement de l\'URL du calendrier:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const calendarStats = await calendarService.getCalendarStats();
      setStats(calendarStats);
    } catch (err) {
      logger.error('Erreur lors du chargement des statistiques:', err);
    }
  };

  const handleCopyUrl = async () => {
    const success = await calendarService.copyToClipboard(calendarUrl);
    if (success) {
      toast.success('URL copiée dans le presse-papier !', {
        position: 'bottom-right',
        autoClose: 2000,
      });
    } else {
      toast.error('Impossible de copier l\'URL', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    }
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir régénérer le lien ? L\'ancien lien ne fonctionnera plus.')) {
      return;
    }

    try {
      setRegenerating(true);
      await calendarService.regenerateCalendarToken();
      await loadCalendarUrl();
      toast.success('Lien régénéré avec succès !', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la régénération';
      toast.error(errorMessage, {
        position: 'bottom-right',
        autoClose: 3000,
      });
      logger.error('Erreur lors de la régénération du token:', err);
    } finally {
      setRegenerating(false);
    }
  };

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Chargement de la synchronisation calendrier...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Icon>
          <FaBell />
        </Icon>
        <Title>Synchronisation avec votre agenda</Title>
      </Header>

      <Description>
        Synchronisez automatiquement vos rappels et rendez-vous avec votre agenda préféré 
        (Google Agenda, Outlook, Apple Calendar, etc.).
      </Description>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {/* Statistiques */}
      <StatsContainer>
        <StatCard>
          <StatValue>{stats.totalEvents}</StatValue>
          <StatLabel>Événements à venir</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.notifications}</StatValue>
          <StatLabel>Notifications</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.actions}</StatValue>
          <StatLabel>Actions</StatLabel>
        </StatCard>
      </StatsContainer>

      {/* URL d'abonnement */}
      <div>
        <label style={{ fontSize: '0.9rem', fontWeight: 500, marginBottom: '0.5rem', display: 'block' }}>
          URL d'abonnement calendrier :
        </label>
        <UrlContainer>
          <UrlInput
            type="text"
            value={calendarUrl}
            readOnly
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
          <IconButton onClick={handleCopyUrl} title="Copier l'URL">
            <span>📋</span>
            <span>Copier</span>
          </IconButton>
        </UrlContainer>
        <RegenerateButton 
          onClick={handleRegenerateToken} 
          disabled={regenerating}
          title="Régénérer le lien (révoque l'ancien accès)"
        >
          <FaSync className={regenerating ? 'fa-spin' : ''} />
          <span>{regenerating ? 'Régénération...' : 'Régénérer le lien'}</span>
        </RegenerateButton>
      </div>

      {/* Instructions */}
      <InstructionsContainer>
        <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
          Comment ajouter à votre agenda ?
        </h4>

        {/* Google Agenda */}
        <InstructionSection>
          <InstructionHeader onClick={() => toggleSection('google')}>
            <InstructionTitle>
              <span style={{ fontSize: '1.2rem' }}>📗</span>
              Google Agenda
            </InstructionTitle>
            {openSection === 'google' ? <span>▲</span> : <span>▼</span>}
          </InstructionHeader>
          <InstructionContent $isOpen={openSection === 'google'}>
            <InstructionSteps>
              <li>Ouvrez <ExternalLink href="https://calendar.google.com" target="_blank" rel="noopener noreferrer">
                Google Agenda ↗
              </ExternalLink></li>
              <li>Cliquez sur le bouton <strong>"+"</strong> à côté de <strong>"Autres agendas"</strong></li>
              <li>Sélectionnez <strong>"À partir de l'URL"</strong></li>
              <li>Collez l'URL d'abonnement ci-dessus</li>
              <li>Cliquez sur <strong>"Ajouter l'agenda"</strong></li>
            </InstructionSteps>
          </InstructionContent>
        </InstructionSection>

        {/* Outlook */}
        <InstructionSection>
          <InstructionHeader onClick={() => toggleSection('outlook')}>
            <InstructionTitle>
              <span style={{ fontSize: '1.2rem' }}>📘</span>
              Outlook / Outlook.com
            </InstructionTitle>
            {openSection === 'outlook' ? <span>▲</span> : <span>▼</span>}
          </InstructionHeader>
          <InstructionContent $isOpen={openSection === 'outlook'}>
            <InstructionSteps>
              <li>Ouvrez <ExternalLink href="https://outlook.live.com/calendar" target="_blank" rel="noopener noreferrer">
                Outlook Calendar ↗
              </ExternalLink></li>
              <li>Cliquez sur <strong>"Ajouter un calendrier"</strong></li>
              <li>Sélectionnez <strong>"S'abonner à partir du web"</strong></li>
              <li>Collez l'URL d'abonnement ci-dessus</li>
              <li>Donnez un nom au calendrier (ex: "ImmoProspect")</li>
              <li>Cliquez sur <strong>"Importer"</strong></li>
            </InstructionSteps>
          </InstructionContent>
        </InstructionSection>

        {/* Apple Calendar */}
        <InstructionSection>
          <InstructionHeader onClick={() => toggleSection('apple')}>
            <InstructionTitle>
              <span style={{ fontSize: '1.2rem' }}>📕</span>
              Apple Calendar (macOS / iOS)
            </InstructionTitle>
            {openSection === 'apple' ? <span>▲</span> : <span>▼</span>}
          </InstructionHeader>
          <InstructionContent $isOpen={openSection === 'apple'}>
            <InstructionSteps>
              <li>Ouvrez l'application <strong>Calendrier</strong></li>
              <li>Dans le menu, sélectionnez <strong>Fichier → Nouvel abonnement au calendrier</strong></li>
              <li>Collez l'URL d'abonnement ci-dessus</li>
              <li>Cliquez sur <strong>"S'abonner"</strong></li>
              <li>Personnalisez le nom et les paramètres si nécessaire</li>
              <li>Cliquez sur <strong>"OK"</strong></li>
            </InstructionSteps>
          </InstructionContent>
        </InstructionSection>

        {/* Autres */}
        <InstructionSection>
          <InstructionHeader onClick={() => toggleSection('other')}>
            <InstructionTitle>
              <span style={{ fontSize: '1.2rem' }}>📙</span>
              Autres applications (Thunderbird, etc.)
            </InstructionTitle>
            {openSection === 'other' ? <span>▲</span> : <span>▼</span>}
          </InstructionHeader>
          <InstructionContent $isOpen={openSection === 'other'}>
            <InstructionSteps>
              <li>Recherchez l'option <strong>"Ajouter un calendrier"</strong> ou <strong>"S'abonner à un calendrier"</strong></li>
              <li>Sélectionnez l'option <strong>"À partir d'une URL"</strong> ou <strong>"iCal/ICS"</strong></li>
              <li>Collez l'URL d'abonnement ci-dessus</li>
              <li>Validez et enregistrez</li>
            </InstructionSteps>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
              <FaCheck style={{ color: '#4CAF50', marginRight: '0.5rem' }} />
              Compatible avec toutes les applications supportant le format iCalendar (ICS)
            </p>
          </InstructionContent>
        </InstructionSection>
      </InstructionsContainer>
    </Container>
  );
};

export default CalendarSync;
